import React from 'react';
import { AttributeMetadata } from '@amzn/controllershiphub-typescript-client';
import { Checkbox, FormField, Input, Select, SpaceBetween } from '@cloudscape-design/components';

/**
 * Props for rendering an input field in the bulk edit form
 * @interface RenderInputFieldProps
 */
interface RenderInputFieldProps {
  attr: AttributeMetadata;
  editData: Record<string, any>;
  setEditData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  isSaving: boolean;
}

/**
 * Renders an appropriate input field based on the attribute metadata
 *
 * @param {RenderInputFieldProps} props - Props for rendering the input field
 * @param {AttributeMetadata} props.attr - Metadata describing the field
 * @param {Record<string, any>} props.editData - Current edit data state
 * @param {React.Dispatch<React.SetStateAction<Record<string, any>>>} props.setEditData - Function to update edit data
 * @param {boolean} props.isSaving - Whether the form is in a saving state
 * @returns {JSX.Element} The rendered input field component
 */
export const renderInputField = ({
  attr,
  editData,
  setEditData,
  isSaving,
}: RenderInputFieldProps) => {
  const fieldName = attr.name || '';
  const displayName = attr.displayName || fieldName;
  const dataType = attr.dataType?.toLowerCase() || '';
  const currentValue = editData[fieldName];

  if (dataType.includes('boolean') || dataType.includes('java.lang.boolean')) {
    const isChecked = currentValue === 'true';
    const isIndeterminate = currentValue === undefined;

    return (
      <FormField label={displayName} key={fieldName} stretch={true}>
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Checkbox
            checked={isChecked}
            indeterminate={isIndeterminate}
            onChange={({ detail }) => {
              let newValue: string | undefined;
              if (isIndeterminate) {
                newValue = 'true';
              } else if (isChecked) {
                newValue = 'false';
              } else {
                newValue = undefined;
              }

              setEditData((prev) => {
                if (newValue === undefined) {
                  const newData = { ...prev };
                  delete newData[fieldName];
                  return newData;
                }
                return { ...prev, [fieldName]: newValue };
              });
            }}
            disabled={isSaving}
          />
        </SpaceBetween>
      </FormField>
    );
  }

  if (dataType.includes('list') || dataType.includes('java.util.list')) {
    const options = [
      { label: '-', value: '-' },
      { label: 'Yes', value: 'Yes' },
      { label: 'No', value: 'No' },
    ];

    return (
      <FormField label={displayName} key={fieldName} stretch={true}>
        <Select
          selectedOption={currentValue ? { label: currentValue, value: currentValue } : null}
          onChange={({ detail }) => {
            const newValue = detail.selectedOption ? detail.selectedOption.value : undefined;
            setEditData((prev) => ({ ...prev, [fieldName]: newValue }));
          }}
          options={options}
          placeholder="Select a value"
          disabled={isSaving}
        />
      </FormField>
    );
  }

  return (
    <FormField label={displayName} key={fieldName} stretch={true}>
      <Input
        value={currentValue !== undefined ? String(currentValue) : ''}
        onChange={({ detail }) =>
          setEditData((prev) => ({ ...prev, [fieldName]: detail.value || undefined }))
        }
        disabled={isSaving}
      />
    </FormField>
  );
};
